import { useEffect, useMemo, useState } from "react";
import {
  arquivarConcurso,
  lerCatalogoAdmin,
  salvarConcurso,
  type ConcursoAdmin,
  type Rascunho,
} from "../lib/mapaConcursos";
import {
  chaveDaBanca,
  dataLonga,
  janelaDeInscricao,
  paraBusca,
  remuneracaoBase,
  remuneracaoPlausivel,
  UFS,
  type Situacao,
} from "../lib/concursos";
import { CAMPO, Recado } from "./ui";
import { htmlDaFolha, type LinhaDaFolha } from "../lib/folhaDeConcursos";
import { IconeEditar, IconeFechar } from "./icones";

/**
 * Admin → Concursos: o catálogo do Mapa de Concursos Contábeis (000120).
 *
 * ## Por que esta aba faz parte do PORTE, e não da fase seguinte
 *
 * A equipe já mantém este catálogo à mão. Medido em 27/08/2026: das 256 linhas do
 * app da Lovable, **194 não existem na planilha de origem** e foram digitadas na
 * tela de Admin dele, em sessões de trabalho de abril a agosto (o intervalo
 * mediano entre dois cadastros consecutivos é de 4,5 minutos — ritmo humano).
 *
 * Sem esta aba, o porte congelaria o catálogo no dia da virada. E aqui a dívida
 * vence rápido: 209 dos 256 concursos JÁ estão com a inscrição encerrada, e em
 * duas semanas a ferramenta estaria mostrando só edital vencido. É a pendência 8
 * do Gerenciador de Estudos com o prazo encurtado.
 *
 * ## As três coisas que esta tela faz diferente da origem
 *
 * **1. Não existe campo de "remuneração mínima".** Na origem são dois campos
 * digitados à mão — o texto e o número —, sem ninguém conferir um contra o outro,
 * e 7 das 256 linhas divergem: "R$ 3.497,52" gravado como 3,49752, "R$ 23.264,47"
 * como 233.264,47. O número é o que ordena e filtra, então o TJ/PR encabeçava a
 * lista dos melhores salários com R$ 233 mil. Aqui o número é DERIVADO do texto,
 * e a tela mostra o que vai gravar antes de gravar.
 *
 * **2. Não existe campo de "status".** A situação sai das datas. Na origem o
 * status é digitado, tem sete grafias para dois significados (incluindo
 * "Incrições Abertas" e "Inhscrições Abertas") e mentia em 218 linhas.
 *
 * **3. Não existe botão de excluir, e não existe botão de "sincronizar".** O
 * catálogo se ARQUIVA — o favorito do aluno aponta para a linha. E o botão de
 * sincronizar da origem faz `delete-all` + insert da planilha: um clique
 * destruiria 194 concursos e todos os favoritos de todos os alunos, sem
 * confirmação. A recarga aqui é `etl/carregar-mapa-concursos.mjs`, que nunca
 * apaga e para sozinha se mais de 20% do catálogo fosse arquivar de uma vez.
 *
 * ## O que esta tela NÃO decide
 *
 * Quem pode editar é `pode_editar('concursos')` (000297), DENTRO das RPCs. A tela não esconde botão para
 * se proteger.
 */

type Filtro = "todos" | Situacao | "arquivados" | "sem_link" | "sem_prova" | "sem_remuneracao";

/**
 * Os alertas de cadastro incompleto.
 *
 * Esta lista foi COPIADA do app de origem (`AdminAlerts.tsx`), e a razão é que
 * ela é o diagnóstico que a própria equipe fez sobre onde o cadastro dela falha.
 * O que mudou: lá o card com contagem zero não é desenhado, então "está tudo bem"
 * e "o alerta não existe" ficam iguais na tela. Aqui o zero aparece dizendo zero.
 */
const ALERTAS: { chave: Filtro; rotulo: string; conta: (c: ConcursoAdmin) => boolean }[] = [
  {
    chave: "a_conferir",
    rotulo: "prazo ilegível",
    conta: (c) => c.ativo && c.situacao === "a_conferir",
  },
  { chave: "sem_link", rotulo: "sem link do edital", conta: (c) => c.ativo && !c.linkEdital },
  { chave: "sem_prova", rotulo: "sem data de prova", conta: (c) => c.ativo && !c.dataProva },
  {
    chave: "sem_remuneracao",
    rotulo: "sem remuneração",
    conta: (c) => c.ativo && c.remuneracao === null,
  },
];

/**
 * O rótulo de cada situação, num lugar só.
 *
 * O app de origem tem QUATRO cópias de `statusConfig` — no cabeçalho, no card,
 * no detalhe e na tabela do admin — e elas JÁ divergem: "Inscrições abertas"
 * num lugar, "Abertas" nos outros três. Rótulo é dado tipado, como
 * `HubDpTextos.tsx` e `SimuladorMeiTextos.tsx`.
 */
const ROTULO_DA_SITUACAO: Record<Situacao, string> = {
  aberta: "Inscrições abertas",
  a_abrir: "Vão abrir",
  a_conferir: "Prazo a conferir",
  encerrada: "Encerradas",
};

const VAZIO: Rascunho = {
  id: null,
  orgao: "",
  cargo: "",
  uf: "",
  cidade: "",
  localProva: "",
  banca: "",
  vagasTexto: "",
  remuneracaoTexto: "",
  jornada: "",
  inscricaoTexto: "",
  dataProva: "",
  linkEdital: "",
  observacoes: "",
};

export default function AdminConcursos() {
  const [lista, setLista] = useState<ConcursoAdmin[] | null>(null);
  const [erro, setErro] = useState(false);
  const [termo, setTermo] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("aberta");
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  const recarregar = async () => {
    const r = await lerCatalogoAdmin();
    if (r === null) setErro(true);
    else {
      setLista(r);
      setErro(false);
    }
  };

  useEffect(() => {
    void recarregar();
  }, []);

  const contagens = useMemo(() => {
    const c = new Map<Filtro, number>();
    if (!lista) return c;
    c.set("todos", lista.filter((x) => x.ativo).length);
    c.set("arquivados", lista.filter((x) => !x.ativo).length);
    for (const s of ["aberta", "a_abrir", "a_conferir", "encerrada"] as Situacao[]) {
      c.set(s, lista.filter((x) => x.ativo && x.situacao === s).length);
    }
    for (const a of ALERTAS) c.set(a.chave, lista.filter(a.conta).length);
    return c;
  }, [lista]);

  const visiveis = useMemo(() => {
    if (!lista) return [];
    return lista.filter((c) => {
      if (filtro === "arquivados") {
        if (c.ativo) return false;
      } else if (filtro === "todos") {
        if (!c.ativo) return false;
      } else {
        const alerta = ALERTAS.find((a) => a.chave === filtro);
        if (alerta) {
          if (!alerta.conta(c)) return false;
        } else if (!c.ativo || c.situacao !== filtro) {
          return false;
        }
      }
      if (termo.trim()) {
        const q = paraBusca(termo);
        const onde = paraBusca(`${c.orgao} ${c.cargo} ${c.banca ?? ""} ${c.cidade ?? ""} ${c.uf}`);
        if (!onde.includes(q)) return false;
      }
      return true;
    });
  }, [lista, filtro, termo]);

  async function gravar(r: Rascunho) {
    const { erro: msg } = await salvarConcurso(r);
    if (msg) {
      setRecado({ tipo: "erro", texto: msg });
      return;
    }
    setRascunho(null);
    setRecado({
      tipo: "ok",
      texto: r.id
        ? "Concurso atualizado, e a conferência foi carimbada com o seu nome e a data de hoje."
        : "Concurso criado, e a conferência foi carimbada com o seu nome e a data de hoje.",
    });
    await recarregar();
  }

  /**
   * A folha imprimível da lista FILTRADA.
   *
   * A origem exporta a lista filtrada em PDF e não diz na folha qual filtro a
   * gerou; aqui o filtro vai no título. E a coluna "Conferido" entra porque o
   * dado é perecível e o papel sobrevive a ele: sem a data ao lado, a lista de
   * hoje e a de três meses atrás são iguais na mesa de alguém.
   */
  function imprimir() {
    const linhas: LinhaDaFolha[] = visiveis.map((c) => ({
      orgao: c.orgao,
      cargo: c.cargo,
      uf: c.uf,
      cidade: c.cidade,
      banca: c.banca,
      situacao: ROTULO_DA_SITUACAO[c.situacao] + (c.ativo ? "" : " (arquivado)"),
      inscricaoTexto: c.inscricaoTexto,
      dataProva: c.dataProva,
      remuneracao: c.remuneracao,
      conferidoEm: c.conferidoEm,
    }));
    const html = htmlDaFolha(linhas, {
      titulo: `Mapa de Concursos — ${ROTULO_DO_FILTRO[filtro] ?? String(filtro)}`,
      geradoEm: new Date().toLocaleString("pt-BR"),
      geradoPor: null,
    });
    // `document.write` numa janela mesma-origem: por isso `htmlDaFolha` escapa
    // todo valor. O bloqueador de pop-up pode recusar, e aí a tela diz — botão
    // que não responde é lido como "o sistema travou".
    const j = window.open("", "_blank", "width=1100,height=800");
    if (!j) {
      setRecado({
        tipo: "erro",
        texto:
          "O navegador bloqueou a janela de impressão. Libere o pop-up para este site e tente de novo.",
      });
      return;
    }
    j.document.write(html);
    j.document.close();
    j.focus();
    j.print();
  }

  async function alternarArquivo(c: ConcursoAdmin) {
    const msg = await arquivarConcurso(c.id, c.ativo);
    if (msg) {
      setRecado({ tipo: "erro", texto: msg });
      return;
    }
    setRecado({
      tipo: "ok",
      texto: c.ativo
        ? `${c.orgao} foi arquivado. Ele sai da tela do aluno e os ${c.favoritos} favorito(s) continuam guardados.`
        : `${c.orgao} voltou para a lista do aluno.`,
    });
    await recarregar();
  }

  if (erro) {
    return (
      <p className="rounded-xl bg-superficie-2 p-6 text-sm leading-relaxed text-cinza">
        Não conseguimos ler o catálogo de concursos. Se você não é administrador, a leitura é
        recusada pelo banco — e a tela não esconde isso de propósito.
      </p>
    );
  }
  if (!lista) return <p className="text-sm text-cinza">Carregando o catálogo…</p>;

  const conferenciaMaisNova = lista
    .map((c) => c.conferidoEm)
    .filter((d): d is string => !!d)
    .sort()
    .at(-1);

  return (
    <section>
      <h2 className="font-display text-lg font-bold text-titulo">Concursos contábeis</h2>
      <p className="mt-1 max-w-3xl text-sm leading-relaxed text-cinza">
        O catálogo do Mapa de Concursos. {contagens.get("todos") ?? 0} concurso(s) na lista do
        aluno e {contagens.get("arquivados") ?? 0} arquivado(s).
        {conferenciaMaisNova && ` A conferência mais recente é de ${dataLonga(conferenciaMaisNova)}.`}
      </p>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-cinza">
        Salvar um concurso é afirmar que você olhou o edital: o carimbo de quem conferiu e
        quando é reescrito a cada salvamento, pelo servidor. A remuneração em número e as datas
        de inscrição são <strong>derivadas</strong> dos textos — não há campo para digitá-las
        em separado.
      </p>

      {/* Os alertas de cadastro incompleto, com o zero à vista. */}
      <div className="mt-4 flex flex-wrap gap-2">
        {ALERTAS.map((a) => {
          const n = contagens.get(a.chave) ?? 0;
          return (
            <button
              key={a.chave}
              type="button"
              onClick={() => setFiltro(a.chave)}
              data-alerta={a.chave}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                n === 0
                  ? "bg-aviso-fundo text-aviso-tinta"
                  : "bg-alerta-fundo text-alerta-tinta"
              }`}
            >
              {n} {a.rotulo}
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-end justify-between gap-3">
        <label className="block min-w-64 flex-1">
          <span className="mb-1.5 block text-sm font-semibold text-titulo">
            Buscar por órgão, cargo, banca ou cidade
          </span>
          <input
            className={CAMPO}
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="Prefeitura de…"
          />
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-contorno" onClick={imprimir} data-imprimir>
            Imprimir esta lista
          </button>
          <button type="button" className="pill-cta" onClick={() => setRascunho(VAZIO)}>
            Cadastrar concurso
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {(
          [
            ["aberta", "Abertas"],
            ["a_abrir", "Vão abrir"],
            ["a_conferir", "A conferir"],
            ["encerrada", "Encerradas"],
            ["todos", "Todos os ativos"],
            ["arquivados", "Arquivados"],
          ] as [Filtro, string][]
        ).map(([chave, rotulo]) => (
          <button
            key={chave}
            type="button"
            onClick={() => setFiltro(chave)}
            aria-pressed={filtro === chave}
            className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
              filtro === chave ? "bg-azul text-white" : "bg-chip text-cinza hover:text-tinta"
            }`}
          >
            {rotulo} <span className="tabular-nums opacity-80">{contagens.get(chave) ?? 0}</span>
          </button>
        ))}
      </div>

      <Distribuicoes lista={lista} />

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      <p className="mt-3 text-sm text-cinza">
        {visiveis.length} concurso(s) com o filtro de agora.
      </p>

      {rascunho && (
        <Formulario
          r={rascunho}
          onMudar={setRascunho}
          onGravar={() => void gravar(rascunho)}
          onCancelar={() => setRascunho(null)}
        />
      )}

      <ul className="mt-4 space-y-2">
        {visiveis.map((c) => (
          <li key={c.id} data-concurso={c.id} className="surface p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-titulo">
                  {c.orgao}
                  {!c.ativo && (
                    <span className="ml-2 rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-cinza">
                      arquivado
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-sm text-cinza">{c.cargo}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-cinza">
                  <span className="font-semibold text-tinta">{c.uf}</span>
                  {c.cidade && <span>{c.cidade}</span>}
                  {c.banca && <span>{c.banca}</span>}
                  <span className="tabular-nums">
                    {c.inscricaoTexto ?? "sem texto de inscrição"}
                  </span>
                  {/* Quantos alunos acompanham. Arquivar um que 12 pessoas
                      seguem é decisão diferente de arquivar um que ninguém
                      olhou — e sem o número na frente ela é tomada às cegas. */}
                  {c.favoritos > 0 && (
                    <span className="rounded-full bg-tile px-2 py-0.5 font-bold text-laranja-texto">
                      {c.favoritos} {c.favoritos === 1 ? "aluno acompanha" : "alunos acompanham"}
                    </span>
                  )}
                  <span>
                    {c.conferidoEm
                      ? `conferido em ${dataLonga(c.conferidoEm)}`
                      : "sem data de conferência"}
                    {c.conferidoPorNome
                      ? ` por ${c.conferidoPorNome}`
                      : c.origemRef
                        ? " (importado)"
                        : ""}
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  className="btn-contorno"
                  data-editar={c.id}
                  onClick={() =>
                    setRascunho({
                      id: c.id,
                      orgao: c.orgao,
                      cargo: c.cargo,
                      uf: c.uf,
                      cidade: c.cidade ?? "",
                      localProva: c.localProva ?? "",
                      banca: c.banca ?? "",
                      vagasTexto: c.vagasTexto ?? "",
                      remuneracaoTexto: c.remuneracaoTexto ?? "",
                      jornada: c.jornada ?? "",
                      inscricaoTexto: c.inscricaoTexto ?? "",
                      dataProva: c.dataProva ?? "",
                      linkEdital: c.linkEdital ?? "",
                      observacoes: c.observacoes ?? "",
                    })
                  }
                >
                  <IconeEditar className="size-4" /> Editar
                </button>
                <button
                  type="button"
                  className="btn-contorno"
                  data-arquivar={c.id}
                  onClick={() => void alternarArquivo(c)}
                >
                  {c.ativo ? "Arquivar" : "Desarquivar"}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {visiveis.length === 0 && (
        <p className="mt-4 rounded-xl bg-superficie-2 p-5 text-sm text-cinza">
          Nenhum concurso com esse filtro. Se você acabou de arquivar tudo de uma categoria,
          isso é o esperado.
        </p>
      )}
    </section>
  );
}

/**
 * O formulário — 14 campos, e nenhum deles é derivado de outro.
 *
 * A origem tem 21. Saíram: `status_lista` e `status_texto` (a situação vem das
 * datas), `remuneracao_min` (vem do texto), `ambito` e `poder` (5% preenchidos
 * nas 256 linhas), `link_inscricao` (uma linha, e ela contém uma DATA), `fonte`
 * (uma linha, e o valor dela é "Inscrições Abertas") e `data_atualizacao` (o
 * carimbo é do servidor).
 *
 * Sete campos a menos por concurso, sobre um catálogo mantido à mão, é meia hora
 * de trabalho por semana que deixa de existir.
 */
function Formulario({
  r,
  onMudar,
  onGravar,
  onCancelar,
}: {
  r: Rascunho;
  onMudar: (r: Rascunho) => void;
  onGravar: () => void;
  onCancelar: () => void;
}) {
  const janela = janelaDeInscricao(r.inscricaoTexto);
  const valor = remuneracaoBase(r.remuneracaoTexto);
  const faltaObrigatorio =
    !r.orgao.trim() || !r.cargo.trim() || !(UFS as readonly string[]).includes(r.uf.toUpperCase());

  const campo = (
    chave: keyof Rascunho,
    rotulo: string,
    ajuda?: string,
    props: React.InputHTMLAttributes<HTMLInputElement> = {},
  ) => (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-titulo">{rotulo}</span>
      <input
        className={CAMPO}
        value={String(r[chave] ?? "")}
        onChange={(e) => onMudar({ ...r, [chave]: e.target.value })}
        data-campo={chave}
        {...props}
      />
      {ajuda && <span className="mt-1 block text-xs leading-relaxed text-cinza">{ajuda}</span>}
    </label>
  );

  return (
    <form
      className="surface mt-4 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        onGravar();
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-base font-bold text-titulo">
          {r.id ? "Editar concurso" : "Cadastrar concurso"}
        </h3>
        <button type="button" onClick={onCancelar} className="text-cinza hover:text-tinta">
          <IconeFechar className="size-5" />
          <span className="sr-only">Fechar</span>
        </button>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {campo("orgao", "Órgão *", 'Como aparece no edital, com "/UF" no fim se for o padrão.')}
        {campo("cargo", "Cargo *")}
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-titulo">Estado *</span>
          <select
            className={CAMPO}
            value={r.uf}
            onChange={(e) => onMudar({ ...r, uf: e.target.value })}
            data-campo="uf"
          >
            <option value="">escolha…</option>
            {UFS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
        {campo("cidade", "Lotação (cidade)")}
        {campo(
          "localProva",
          "Local de prova",
          'Só preencha se for DIFERENTE de "cidade/UF" — por exemplo quando a prova é em várias cidades. Igual, deixe em branco: a tela mostra a cidade.',
        )}
        {campo("banca", "Banca")}
        {campo("vagasTexto", "Vagas", 'Como no edital: "1 + CR", "CR", "21 + CR". "CR" conta como zero vaga imediata.')}
        {campo("jornada", "Jornada", 'Como no edital: "40h", "30h".')}
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          {campo(
            "remuneracaoTexto",
            "Remuneração",
            'Como no edital, com os adicionais: "R$ 5.215,39 + R$ 1.175,00 (Auxílio Alimentação)".',
          )}
          {/* A PRÉVIA do que vai ser gravado. Sem ela, o operador digita o texto
              e não sabe qual número vai ordenar a lista do aluno — que é
              exatamente onde a origem errou em 7 linhas. */}
          <p className="mt-1.5 text-xs leading-relaxed" data-previa="remuneracao">
            {r.remuneracaoTexto.trim() === "" ? (
              <span className="text-cinza">Sem remuneração: o concurso fica fora do filtro de salário.</span>
            ) : valor === null ? (
              <span className="text-alerta-tinta">
                Não achamos número neste texto. O concurso entra sem valor e fica fora da
                ordenação por salário.
              </span>
            ) : !remuneracaoPlausivel(valor) ? (
              <span className="text-erro-tinta">
                Vamos gravar{" "}
                <strong>
                  {valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                </strong>
                . Isso está fora da faixa de salário de concurso — confira a vírgula antes de
                salvar.
              </span>
            ) : (
              <span className="text-cinza">
                Vamos gravar{" "}
                <strong className="text-tinta">
                  {valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                </strong>{" "}
                — é este número que ordena e filtra a lista do aluno.
              </span>
            )}
          </p>
        </div>

        <div>
          {campo(
            "inscricaoTexto",
            "Inscrições",
            'Como no edital, com as horas: "16h 20/05/2026 a 18/06/2026 16h".',
          )}
          {/* A PRÉVIA da janela. É o que substitui os dois campos de data da
              origem — e o que mostra ao operador quando o texto não dá para ler,
              antes de o aluno ver "confira no edital". */}
          <p className="mt-1.5 text-xs leading-relaxed" data-previa="janela">
            {r.inscricaoTexto.trim() === "" ? (
              <span className="text-cinza">
                Sem texto: o concurso aparece como &ldquo;confira no edital&rdquo;.
              </span>
            ) : janela ? (
              <span className="text-cinza">
                Lemos{" "}
                <strong className="text-tinta">
                  {dataLonga(janela.inicio)} a {dataLonga(janela.fim)}
                </strong>
                . É desta janela que sai &ldquo;inscrições abertas&rdquo;.
              </span>
            ) : (
              <span className="text-alerta-tinta">
                Não conseguimos ler duas datas coerentes neste texto — o aluno vai ver
                &ldquo;confira no edital&rdquo;. Se as datas estão trocadas (fim antes do
                início), corrija; o texto continua aparecendo como você escrever.
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {campo("dataProva", "Data da prova", undefined, { type: "date" })}
        {campo(
          "linkEdital",
          "Link do edital",
          "Precisa começar com https:// — o banco recusa qualquer outra coisa, porque este endereço vira um link na tela do aluno.",
        )}
      </div>

      <label className="mt-4 block">
        <span className="mb-1.5 block text-sm font-semibold text-titulo">Observações</span>
        <textarea
          className={`${CAMPO} min-h-20`}
          value={r.observacoes}
          onChange={(e) => onMudar({ ...r, observacoes: e.target.value })}
          data-campo="observacoes"
        />
      </label>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button type="submit" className="pill-cta" disabled={faltaObrigatorio}>
          {r.id ? "Salvar e carimbar a conferência" : "Cadastrar"}
        </button>
        <button type="button" className="btn-contorno" onClick={onCancelar}>
          Cancelar
        </button>
        {/* A recusa NÃO é silenciosa. Na origem o botão faz `return` sem toast,
            sem mensagem e sem marcar o campo: clicar em "Criar" sem UF
            simplesmente não faz nada, e botão que não responde é lido como "o
            sistema travou". */}
        {faltaObrigatorio && (
          <span className="text-sm text-alerta-tinta">
            Falta preencher órgão, cargo e estado.
          </span>
        )}
      </div>
    </form>
  );
}

/** O rótulo de cada filtro — vai para o título da folha impressa. */
const ROTULO_DO_FILTRO: Record<string, string> = {
  aberta: "inscrições abertas",
  a_abrir: "vão abrir",
  a_conferir: "prazo a conferir",
  encerrada: "encerradas",
  todos: "todos os ativos",
  arquivados: "arquivados",
  sem_link: "sem link do edital",
  sem_prova: "sem data de prova",
  sem_remuneracao: "sem remuneração",
};

/**
 * As distribuições do catálogo — por situação, por estado e por banca.
 *
 * ## Por que sem biblioteca de gráfico
 *
 * A origem usa recharts para quatro gráficos. Três deles são contagens de uma
 * coluna, e barra proporcional feita de `div` diz a mesma coisa — é o que a
 * CASCATA do HUB DP já faz. Trazer uma dependência de 500 kB para desenhar
 * três listas ordenadas não se paga.
 *
 * ## O quarto gráfico da origem NÃO foi portado, e isso é a favor
 *
 * É a "Evolução de Atividades nos últimos 30 dias", construída sobre o log do
 * admin — que a origem lê com `.limit(100)` e SEM filtro de data. Com mais de
 * 100 eventos no período, ele desenha zero nos dias antigos: o gráfico mente
 * por desenho. Portar exigiria primeiro um log de atividade no hub (que não
 * existe — aqui há o carimbo de quem conferiu cada linha, que é outra coisa),
 * e depois não repetir o erro do limite.
 *
 * ## As cores
 *
 * Uma só, e a proporção é o comprimento. Barra colorida por categoria pediria
 * uma paleta de 10 tons distinguíveis entre si E contra o fundo nos dois temas
 * — e a medição das cores de setor do Calendário já disse que isso não existe
 * na paleta da marca.
 */
function Distribuicoes({ lista }: { lista: ConcursoAdmin[] }) {
  const ativos = lista.filter((c) => c.ativo);
  if (ativos.length === 0) return null;

  const contar = <T,>(f: (c: ConcursoAdmin) => T | null) => {
    const m = new Map<T, number>();
    for (const c of ativos) {
      const k = f(c);
      if (k === null) continue;
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  };

  const porSituacao = (
    ["aberta", "a_abrir", "a_conferir", "encerrada"] as Situacao[]
  ).map((s) => [ROTULO_DA_SITUACAO[s], ativos.filter((c) => c.situacao === s).length] as const);
  const porUf = contar((c) => c.uf).slice(0, 10);
  // A banca agrupa pela CHAVE (sem acento e sem caixa) e mostra a grafia mais
  // comum: são 128 grafias para ~114 bancas, com 14 grupos que são a mesma
  // casa escrita de dois jeitos (Vunesp/VUNESP, IMESO/Imeso...).
  const grafias = new Map<string, Map<string, number>>();
  for (const c of ativos) {
    if (!c.banca) continue;
    const k = chaveDaBanca(c.banca);
    if (!grafias.has(k)) grafias.set(k, new Map());
    const g = grafias.get(k)!;
    g.set(c.banca, (g.get(c.banca) ?? 0) + 1);
  }
  const porBanca = [...grafias.values()]
    .map((g) => {
      const total = [...g.values()].reduce((a, b) => a + b, 0);
      const nome = [...g.entries()].sort((a, b) => b[1] - a[1])[0][0];
      return [nome, total] as const;
    })
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  return (
    <section role="group" aria-labelledby="titulo-distribuicoes" className="mt-6">
      <h3 id="titulo-distribuicoes" className="text-sm font-semibold text-titulo">
        Como o catálogo está distribuído
      </h3>
      <p className="pt-0.5 text-xs text-cinza">
        Sobre os {ativos.length} concursos ativos. A situação é derivada das datas, então
        estes números mudam sozinhos com o calendário.
      </p>
      <div className="mt-3 grid gap-5 sm:grid-cols-3">
        <Barras titulo="Por situação" dados={porSituacao} total={ativos.length} />
        <Barras titulo="Por estado (10 maiores)" dados={porUf} total={ativos.length} />
        <Barras titulo="Por banca (8 maiores)" dados={porBanca} total={ativos.length} />
      </div>
    </section>
  );
}

function Barras({
  titulo,
  dados,
  total,
}: {
  titulo: string;
  dados: readonly (readonly [string, number])[];
  total: number;
}) {
  const maior = Math.max(1, ...dados.map(([, n]) => n));
  return (
    <div className="rounded-cf bg-superficie-2 p-4">
      <h4 className="text-xs font-semibold uppercase tracking-[0.09em] text-cinza">{titulo}</h4>
      <ul className="mt-2 space-y-1.5">
        {dados.length === 0 ? (
          <li className="text-xs text-cinza">Nada a mostrar.</li>
        ) : (
          dados.map(([nome, n]) => (
            <li key={nome}>
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="min-w-0 truncate text-tinta" title={nome}>
                  {nome}
                </span>
                <span className="shrink-0 tabular-nums font-semibold text-tinta">
                  {n}
                  <span className="pl-1 font-normal text-cinza">
                    {Math.round((n / total) * 100)}%
                  </span>
                </span>
              </div>
              {/* `role="img"` com `aria-label`: a barra é a mesma informação do
                  número ao lado, e sem isto o leitor de tela anunciaria uma
                  caixa vazia. */}
              <div
                role="img"
                aria-label={`${nome}: ${n} de ${total}`}
                className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-borda"
              >
                <div
                  className="h-full rounded-full bg-azul"
                  style={{ width: `${Math.max(2, (n / maior) * 100)}%` }}
                />
              </div>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
